import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { loadClientSelection } from '../../canopy-ui-components/helpers/localStorage';
const initialState: ClientsState = loadClientSelection() || {
    portfolio: { users: [], groups: [], selectedUser: null, viewType: null },
    'excel-addin': {
        users: [],
        groups: [],
        selectedUser: null,
        viewType: null,
    },
    'review-all': {
        users: [],
        groups: [],
        selectedUser: null,
        viewType: null,
    },
};

export function UUID(): string {
    let dt = new Date().getTime();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
        /[xy]/g,
        function (c) {
            const r = (dt + Math.random() * 16) % 16 | 0;
            dt = Math.floor(dt / 16);
            return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
        }
    );
}

const getSelectedGroup = (group: PortfolioGroup) => {
    const groupUserIds = group?.users
        ?.map((u: PortfolioUser) => u.id)
        .join(',');
    const groupUserMeta = group?.users
        ?.map((u: PortfolioUser) => u.displayName)
        .join(',');
    return {
        uniqueId: group.uniqueId,
        id: groupUserIds,
        displayName: group.name || '',
        username: group.name || '',
        meta: groupUserMeta || '',
        users: group.users,
        type: 'GROUPED_CLIENT',
    };
};
const clientsSlice = createSlice({
    name: 'clients',
    initialState: initialState,
    reducers: {
        setUsers(
            state: ClientsState,
            action: PayloadAction<{
                type: ClientsTypes;
                users: PortfolioUser[];
            }>
        ) {
            const { type, users } = action.payload;
            return {
                ...state,
                [type]: {
                    ...state[type],
                    users: users.map((user: PortfolioUser) => {
                        return {
                            ...user,
                            uniqueId: !user?.uniqueId ? UUID() : user?.uniqueId,
                            meta: user.username,
                            type: 'SINGLE_CLIENT',
                        };
                    }),
                },
            };
        },
        setGroups(state: ClientsState, action: any) {
            const {
                type,
                groups,
            }: { type: ClientsTypes; groups: PortfolioGroup[] } =
                action.payload;
            return {
                ...state,
                [type]: {
                    ...state[type],
                    groups: groups.filter(
                        ({ users }: PortfolioGroup) => users?.length !== 0
                    ),
                },
            };
        },
        addAndSelectUser(state: ClientsState, action: any) {
            const { type, user }: { type: ClientsTypes; user: PortfolioUser } =
                action.payload;
            let oldUser: any = state[type].users.find(
                (item: PortfolioUser) => item.id === user.id
            );
            if (!oldUser) {
                oldUser = {
                    ...action.payload.user,
                    uniqueId: UUID(),
                    meta: action.payload.displayName,
                    type: 'SINGLE_CLIENT',
                };
                state[type].users.push({ ...oldUser });
            }
            state[type].selectedUser = { ...oldUser };
        },
        removeUser(state: ClientsState, action: any) {
            const { type, user }: { type: ClientsTypes; user: PortfolioUser } =
                action.payload;
            const { selectedUser } = current(state[type]);
            if (user?.type === 'SINGLE_CLIENT') {
                const userIdx = state[type].users.findIndex(
                    (item: PortfolioUser) => item.uniqueId === user.uniqueId
                );
                if (userIdx !== -1) {
                    state[type].users.splice(userIdx, 1);
                }
                if (selectedUser?.uniqueId === user.uniqueId) {
                    if (state[type]?.users?.length) {
                        state[type].selectedUser = state[type].users[0];
                    } else if (state[type]?.groups?.length) {
                        state[type].selectedUser = getSelectedGroup(
                            state[type].groups[0]
                        );
                    } else {
                        state[type].selectedUser = null;
                    }
                }
                return state;
            }
            if (user?.type === 'GROUPED_CLIENT') {
                const groupIdx = state[type].groups.findIndex(
                    (group: PortfolioGroup) => group.uniqueId === user.uniqueId
                );
                if (groupIdx !== -1) {
                    state[type].groups.splice(groupIdx, 1);
                }
                if (selectedUser?.uniqueId === user.uniqueId) {
                    if (state[type]?.groups?.length) {
                        state[type].selectedUser = getSelectedGroup(
                            state[type].groups[0]
                        );
                    } else if (state[type]?.users?.length) {
                        state[type].selectedUser = state[type].users[0];
                    } else {
                        state[type].selectedUser = null;
                    }
                }
                return state;
            }
            return state;
        },
        updateSelectedUser(state: ClientsState, action: any) {
            const { type }: { type: ClientsTypes; user: PortfolioUser } =
                action.payload;
            const { users, groups, selectedUser } = current(state[type]);
            let newSelectedUser: any = null;

            if (!users?.length && !groups?.length && !selectedUser) {
                return {
                    ...state,
                    [type]: { ...state[type], selectedUser: null },
                };
            }

            if (selectedUser?.type === 'SINGLE_CLIENT') {
                if (users?.length) {
                    newSelectedUser = users?.find(
                        ({ uniqueId }) => uniqueId === selectedUser?.uniqueId
                    );
                    if (!newSelectedUser) {
                        newSelectedUser = users[0];
                    }
                    return {
                        ...state,
                        [type]: {
                            ...state[type],
                            selectedUser: newSelectedUser,
                        },
                    };
                }
                newSelectedUser = getSelectedGroup(groups[0]);
                return {
                    ...state,
                    [type]: { ...state[type], selectedUser: newSelectedUser },
                };
            }

            if (selectedUser?.type === 'GROUPED_CLIENT') {
                let newGroupUser;
                if (groups?.length) {
                    newGroupUser = groups?.find(
                        ({ uniqueId }) => uniqueId === selectedUser?.uniqueId
                    );
                    if (!newGroupUser) {
                        newGroupUser = groups[0];
                    }
                    if (newGroupUser) {
                        newSelectedUser = getSelectedGroup(newGroupUser);
                    }
                    return {
                        ...state,
                        [type]: {
                            ...state[type],
                            selectedUser: newSelectedUser,
                        },
                    };
                }
                newSelectedUser = users[0];
                return {
                    ...state,
                    [type]: { ...state[type], selectedUser: newSelectedUser },
                };
            }
            if (users?.length) {
                newSelectedUser = { ...users[0] };
                return {
                    ...state,
                    [type]: { ...state[type], selectedUser: newSelectedUser },
                };
            }
            if (groups?.length) {
                newSelectedUser = getSelectedGroup(groups[0]);
                return {
                    ...state,
                    [type]: { ...state[type], selectedUser: newSelectedUser },
                };
            }
        },
        setSelectedUser(state: ClientsState, action: any) {
            const { type, user }: { type: ClientsTypes; user: PortfolioUser } =
                action.payload;

            if (user) {
                return {
                    ...state,
                    [type]: { ...state[type], selectedUser: user },
                };
            }
            let newSelectedUser: any = null;
            const { users, groups } = current(state[type]);
            if (users?.length) {
                newSelectedUser = users[0];
            } else if (groups?.length) {
                newSelectedUser = getSelectedGroup(groups[0]);
            }

            return {
                ...state,
                [type]: { ...state[type], selectedUser: newSelectedUser },
            };
        },
        resetPortfolioClientSelection(state: ClientsState) {
            return {
                ...state,
                portfolio: {
                    users: [],
                    groups: [],
                    selectedUser: null,
                    viewType: null,
                },
                'excel-addin': {
                    users: [],
                    groups: [],
                    selectedUser: null,
                    viewType: null,
                },
            };
        },
    },
});

export const {
    setUsers,
    setGroups,
    setSelectedUser,
    updateSelectedUser,
    removeUser,
    addAndSelectUser,
    resetPortfolioClientSelection,
} = clientsSlice.actions;

export default clientsSlice;
